import type {
  EchoflicksFramingQcSummary,
  EchoflicksFramingSubjectSummary,
  EchoflicksSessionFeedback,
  EchoflicksSessionFramingEvent,
} from "../types";

export type EchoFeedbackCorrelation = {
  sceneId: string | null;
  attemptId: string | null;
  taskId: string | null;
  framingPlanId: string | null;
  planDigest: string | null;
  videoFrame: number | null;
  timelineMatch: "exact_interval" | "unavailable";
};

export type FramingSceneFilter =
  | "all"
  | "at_risk"
  | "failed_qc"
  | "wrong_subject"
  | "face"
  | "pet"
  | "object"
  | "tracking"
  | "contain"
  | "repaired"
  | "crop_feedback";

export type FramingSceneView = {
  sceneId: string;
  planId?: string | null;
  planDigest?: string | null;
  planRevision?: number | null;
  attemptId?: string | null;
  taskId?: string | null;
  receiptId?: string | null;
  intentSource?: string | null;
  intentMode?: string | null;
  subjects: EchoflicksFramingSubjectSummary[];
  strategy?: string | null;
  plannedStrategy?: string | null;
  appliedStrategy?: string | null;
  providerAspect?: string | null;
  deliveryAspect?: string | null;
  sourceRef?: string | null;
  qc?: EchoflicksFramingQcSummary | null;
  repaired: boolean;
  fallback: boolean;
  fallbackReason?: string | null;
  latencyMs?: number | null;
  costUsd?: number | null;
  latestAt?: string | null;
  attemptCount: number;
  events: EchoflicksSessionFramingEvent[];
};

export const FILTER_LABELS: Record<FramingSceneFilter, string> = {
  all: "All scenes",
  at_risk: "At risk",
  failed_qc: "Failed QC",
  wrong_subject: "Wrong / missing subject",
  face: "People",
  pet: "Pets",
  object: "Objects",
  tracking: "Tracking crop",
  contain: "Contain / postcard",
  repaired: "Repaired",
  crop_feedback: "Crop-related feedback",
};

const WRONG_SUBJECT_CODES = new Set([
  "identity_mismatch",
  "subject_missing",
  "wrong_subject",
  "protected_subject_missing",
]);

const CONTAIN_STRATEGIES = new Set([
  "contain",
  "contain_blur",
  "postcard",
  "static_source",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function payloadSubjects(
  event: EchoflicksSessionFramingEvent,
): EchoflicksFramingSubjectSummary[] {
  const subjects = event.payload?.subjects;
  if (!Array.isArray(subjects)) return event.subject_summary ?? [];
  return subjects.filter(isRecord).map((subject) => ({
    subject_id: nullableString(subject.subject_id),
    label: nullableString(subject.label),
    kind: nullableString(subject.kind),
    priority: nullableString(subject.priority),
    confidence: nullableNumber(subject.confidence),
    detector: nullableString(subject.detector),
    coordinate_space_id: nullableString(subject.coordinate_space_id),
    identity_required:
      typeof subject.identity_required === "boolean"
        ? subject.identity_required
        : null,
  }));
}

function mergeSubjects(
  existing: EchoflicksFramingSubjectSummary[],
  incoming: EchoflicksFramingSubjectSummary[],
): EchoflicksFramingSubjectSummary[] {
  const byId = new Map<string, EchoflicksFramingSubjectSummary>();
  for (const subject of [...existing, ...incoming]) {
    const key =
      subject.subject_id ?? subject.label ?? `${subject.kind}:unknown`;
    const defined = Object.fromEntries(
      Object.entries(subject).filter(([, value]) => value != null),
    ) as EchoflicksFramingSubjectSummary;
    byId.set(key, { ...byId.get(key), ...defined });
  }
  return [...byId.values()];
}

type EventCorrelation = {
  attemptId: string | null;
  taskId: string | null;
  planDigest: string | null;
};

function eventCorrelation(
  event: EchoflicksSessionFramingEvent,
): EventCorrelation {
  const payload = event.payload ?? {};
  const receipt = isRecord(payload.receipt)
    ? payload.receipt
    : isRecord(payload.applied_receipt)
      ? payload.applied_receipt
      : {};
  return {
    attemptId:
      event.attempt_id ??
      nullableString(payload.attempt_id) ??
      nullableString(receipt.attempt_id),
    taskId:
      event.task_id ??
      nullableString(payload.task_id) ??
      nullableString(receipt.task_id),
    planDigest:
      event.plan_digest ??
      nullableString(payload.plan_digest) ??
      nullableString(receipt.plan_digest),
  };
}

function finiteTimelineSeconds(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 86_400
    ? value
    : null;
}

type TimelineCandidate = {
  event: EchoflicksSessionFramingEvent;
  start: number;
  end: number;
};

export type FeedbackSceneCorrelationStatus =
  | "derived"
  | "ambiguous"
  | "unmatched";

export type EchoFeedbackSceneCandidates = {
  /**
   * "derived": exactly one scene interval contains the timestamp; the match
   * is derived from current events, not a frozen snapshot. "ambiguous":
   * multiple distinct scenes claim the timestamp. "unmatched": none do.
   */
  status: FeedbackSceneCorrelationStatus;
  candidateSceneIds: string[];
  correlation: EchoFeedbackCorrelation;
};

/**
 * Correlate a delivered-video timestamp against the half-open scene
 * intervals `[start, end)` in compact encoded-delivery telemetry, reporting
 * candidates honestly instead of picking by array order. This helper never
 * reconstructs crop geometry.
 */
export function correlateFeedbackSceneCandidates(
  events: EchoflicksSessionFramingEvent[] = [],
  timestampSeconds: number,
): EchoFeedbackSceneCandidates {
  const candidates: TimelineCandidate[] = [];
  for (const event of events) {
    if (event.scene_id == null) continue;
    const payload = event.payload ?? {};
    const start = finiteTimelineSeconds(
      event.timeline_start_seconds ?? payload.timeline_start_seconds,
    );
    const end = finiteTimelineSeconds(
      event.timeline_end_seconds ?? payload.timeline_end_seconds,
    );
    if (start == null || end == null || end <= start) continue;
    if (timestampSeconds >= start && timestampSeconds < end) {
      candidates.push({ event, start, end });
    }
  }
  const sceneIds = [
    ...new Set(candidates.map(({ event }) => String(event.scene_id))),
  ].sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true }),
  );
  if (sceneIds.length !== 1) {
    return {
      status: sceneIds.length === 0 ? "unmatched" : "ambiguous",
      candidateSceneIds: sceneIds,
      correlation: {
        sceneId: null,
        attemptId: null,
        taskId: null,
        framingPlanId: null,
        planDigest: null,
        videoFrame: null,
        timelineMatch: "unavailable",
      },
    };
  }
  const selected = [...candidates].sort(
    (left, right) =>
      String(right.event.occurred_at ?? "").localeCompare(
        String(left.event.occurred_at ?? ""),
      ) ||
      Number(Boolean(eventCorrelation(right.event).attemptId)) -
        Number(Boolean(eventCorrelation(left.event).attemptId)),
  )[0];
  const correlation = eventCorrelation(selected.event);
  const payload = selected.event.payload ?? {};
  const receipt = isRecord(payload.receipt)
    ? payload.receipt
    : isRecord(payload.applied_receipt)
      ? payload.applied_receipt
      : {};
  const fps = nullableNumber(payload.fps ?? receipt.fps);
  return {
    status: "derived",
    candidateSceneIds: sceneIds,
    correlation: {
      sceneId: String(selected.event.scene_id),
      attemptId: correlation.attemptId,
      taskId: correlation.taskId,
      framingPlanId:
        selected.event.framing_plan_id ??
        nullableString(payload.plan_id) ??
        nullableString(receipt.plan_id),
      planDigest: correlation.planDigest,
      videoFrame: fps && fps > 0 ? Math.floor(timestampSeconds * fps) : null,
      timelineMatch: "exact_interval",
    },
  };
}

/**
 * Correlate a delivered-video timestamp only when compact encoded-delivery
 * telemetry identifies one unambiguous scene interval. Legacy reports remain
 * explicitly uncorrelated; this helper never reconstructs crop geometry.
 */
export function correlateFeedbackToFraming(
  events: EchoflicksSessionFramingEvent[] = [],
  timestampSeconds: number,
): EchoFeedbackCorrelation {
  return correlateFeedbackSceneCandidates(events, timestampSeconds).correlation;
}

function eventMatchesCurrent(
  event: EchoflicksSessionFramingEvent,
  current: EventCorrelation,
): boolean {
  const candidate = eventCorrelation(event);
  const pairs = [
    [current.attemptId, candidate.attemptId],
    [current.taskId, candidate.taskId],
    [current.planDigest, candidate.planDigest],
  ] as const;
  const conflict = pairs.some(
    ([expected, actual]) => expected && actual && expected !== actual,
  );
  const sharedIdentity = pairs.some(
    ([expected, actual]) => expected && actual && expected === actual,
  );
  return !conflict && sharedIdentity;
}

function eventOrder(
  left: EchoflicksSessionFramingEvent,
  right: EchoflicksSessionFramingEvent,
): number {
  return (
    String(left.occurred_at ?? "").localeCompare(
      String(right.occurred_at ?? ""),
    ) || String(left.id).localeCompare(String(right.id))
  );
}

export function buildFramingScenes(
  events: EchoflicksSessionFramingEvent[] = [],
): FramingSceneView[] {
  const grouped = new Map<string, EchoflicksSessionFramingEvent[]>();
  for (const event of [...events].sort(eventOrder)) {
    if (event.scene_id == null) continue;
    const sceneId = String(event.scene_id);
    grouped.set(sceneId, [...(grouped.get(sceneId) ?? []), event]);
  }

  const scenes = [...grouped].map(([sceneId, sceneEvents]) => {
    const anchorEvent = [...sceneEvents].reverse().find((event) => {
      const correlation = eventCorrelation(event);
      return Boolean(
        correlation.attemptId || correlation.taskId || correlation.planDigest,
      );
    });
    const anchor = anchorEvent
      ? eventCorrelation(anchorEvent)
      : { attemptId: null, taskId: null, planDigest: null };
    const currentEvents = anchorEvent
      ? sceneEvents.filter((event) => eventMatchesCurrent(event, anchor))
      : sceneEvents;
    const current: FramingSceneView = {
      sceneId,
      attemptId: anchor.attemptId,
      taskId: anchor.taskId,
      planDigest: anchor.planDigest,
      subjects: [],
      repaired: false,
      fallback: false,
      attemptCount:
        new Set(
          sceneEvents
            .map((event) => eventCorrelation(event).attemptId)
            .filter(Boolean),
        ).size || 1,
      events: sceneEvents,
    };

    for (const event of currentEvents) {
      const payload = event.payload ?? {};
      const receipt = isRecord(payload.receipt)
        ? payload.receipt
        : isRecord(payload.applied_receipt)
          ? payload.applied_receipt
          : {};
      const correlation = eventCorrelation(event);
      const latencyMs = nullableNumber(
        payload.latency_ms ?? payload.elapsed_ms,
      );
      const costUsd = nullableNumber(
        payload.cost_usd ?? payload.estimated_cost_usd,
      );
      current.planId =
        event.framing_plan_id ??
        nullableString(payload.plan_id) ??
        nullableString(receipt.plan_id) ??
        current.planId;
      current.planDigest = correlation.planDigest ?? current.planDigest;
      current.planRevision =
        event.plan_revision ??
        nullableNumber(payload.plan_revision) ??
        nullableNumber(receipt.plan_revision) ??
        current.planRevision;
      current.attemptId = correlation.attemptId ?? current.attemptId;
      current.taskId = correlation.taskId ?? current.taskId;
      current.receiptId =
        event.receipt_id ??
        nullableString(payload.receipt_id) ??
        nullableString(receipt.receipt_id) ??
        current.receiptId;
      current.intentSource = event.intent_source ?? current.intentSource;
      current.intentMode = nullableString(payload.mode) ?? current.intentMode;
      current.subjects = mergeSubjects(
        current.subjects,
        payloadSubjects(event),
      );
      if (event.event_type === "scene.framing.plan_resolved") {
        current.plannedStrategy = event.strategy ?? current.plannedStrategy;
      }
      if (event.event_type === "scene.framing.render_applied") {
        current.appliedStrategy =
          nullableString(receipt.strategy) ??
          event.strategy ??
          current.appliedStrategy;
      }
      current.strategy = event.strategy ?? current.strategy;
      current.providerAspect = event.provider_aspect ?? current.providerAspect;
      current.deliveryAspect = event.delivery_aspect ?? current.deliveryAspect;
      current.sourceRef =
        nullableString(payload.working_key ?? payload.raw_key) ??
        current.sourceRef;
      current.qc = event.qc_summary ?? current.qc;
      current.repaired =
        current.repaired || event.event_type === "scene.framing.repair_applied";
      current.fallback =
        current.fallback ||
        event.event_type === "scene.framing.fallback_applied";
      current.fallbackReason = event.fallback_reason ?? current.fallbackReason;
      current.latencyMs = latencyMs ?? current.latencyMs;
      current.costUsd = costUsd ?? current.costUsd;
      current.latestAt = event.occurred_at ?? current.latestAt;
    }
    current.strategy =
      current.appliedStrategy ?? current.plannedStrategy ?? current.strategy;
    return current;
  });

  return scenes.sort((left, right) =>
    left.sceneId.localeCompare(right.sceneId, undefined, { numeric: true }),
  );
}

function isSceneAtRisk(scene: FramingSceneView): boolean {
  return (
    scene.qc?.status === "failed" ||
    (scene.qc?.issue_codes?.length ?? 0) > 0 ||
    scene.repaired ||
    scene.fallback
  );
}

export function filterFramingScenes(
  scenes: FramingSceneView[],
  filter: FramingSceneFilter,
  hasCropFeedback = false,
  cropFeedbackSceneIds: ReadonlySet<string> = new Set(),
): FramingSceneView[] {
  if (filter === "all") return scenes;
  if (filter === "crop_feedback") {
    // Crop feedback with zero correlated scenes matches zero specific scenes.
    // Uncorrelated feedback belongs in its own bucket; claiming every scene
    // turned unknown correlation into false specificity (CROP-WB-004).
    if (!hasCropFeedback) return [];
    return scenes.filter((scene) => cropFeedbackSceneIds.has(scene.sceneId));
  }
  return scenes.filter((scene) => {
    const kinds = new Set(scene.subjects.map((subject) => subject.kind));
    const issues = new Set(scene.qc?.issue_codes ?? []);
    if (filter === "at_risk") return isSceneAtRisk(scene);
    if (filter === "failed_qc") return scene.qc?.status === "failed";
    if (filter === "wrong_subject") {
      return [...issues].some((issue) => WRONG_SUBJECT_CODES.has(issue));
    }
    if (filter === "face") return kinds.has("person") || kinds.has("group");
    if (filter === "pet") return kinds.has("pet");
    if (filter === "object") return kinds.has("object");
    if (filter === "tracking") return scene.strategy === "tracking_crop";
    if (filter === "contain") {
      return scene.strategy != null && CONTAIN_STRATEGIES.has(scene.strategy);
    }
    return scene.repaired;
  });
}

export function hasCropRelatedFeedback(
  feedback: EchoflicksSessionFeedback[] = [],
): boolean {
  return feedback.some(isCropRelatedFeedback);
}

export function isCropRelatedFeedback(
  entry: EchoflicksSessionFeedback,
): boolean {
  if (entry.feedback_category?.trim()) {
    return (
      entry.feedback_category === "crop_framing" ||
      entry.feedback_category === "framing_crop"
    );
  }
  return /\b(crop(?:ped|ping)?|framing|cut[ -]?off|off[- ]?cent(?:er|re)|not cent(?:er(?:ed)?|re(?:d)?)|missing subject|zoom(?:ed|ing)?|too close|out of frame|black bars?|aspect ratio|portrait|landscape|vertical|horizontal|stretched|squashed|recort\w*|cortad\w*|encuadr\w*|centrad\w*|recadr\w*|cadrage|enquadr\w*|centraliz\w*)\b/i.test(
    entry.feedback_text ?? "",
  );
}
